import type {
  FieldValues,
  FormOptions,
  FormStore,
  ResponseData,
  FormProps,
  FieldPathValue,
  FieldPath,
  MaybeValue,
  PartialKey,
  FieldProps,
  FieldArrayPath,
  FieldArrayProps,
} from "@modular-forms/qwik";
import {
  getErrors,
  reset,
  useForm as originalUseForm,
} from "@modular-forms/qwik";
import { JSXOutput, useTask$ } from "@builder.io/qwik";
import { toast } from "qwik-sonner";

export const hasError = <T extends FieldValues>(
  form: FormStore<T, ResponseData>,
): boolean => {
  const errors = getErrors(form, { shouldActive: false });
  const hasErrors = Object.values(errors).some(Boolean);
  if (hasErrors) {
    console.warn("Form errors", errors);
  }

  return hasErrors;
};

export function useForm<
  TFieldValues extends FieldValues,
  TResponseData extends ResponseData = undefined,
>(
  options: FormOptions<TFieldValues, TResponseData>,
): [
  FormStore<TFieldValues, TResponseData>,
  {
    Form: (
      props: Omit<FormProps<TFieldValues, TResponseData>, "of" | "action">,
    ) => JSXOutput;
    Field: <TFieldName extends FieldPath<TFieldValues>>(
      props: FieldPathValue<TFieldValues, TFieldName> extends MaybeValue<string>
        ? PartialKey<
            Omit<FieldProps<TFieldValues, TResponseData, TFieldName>, "of">,
            "type"
          >
        : Omit<FieldProps<TFieldValues, TResponseData, TFieldName>, "of">,
    ) => JSXOutput;
    FieldArray: <TFieldArrayName extends FieldArrayPath<TFieldValues>>(
      props: Omit<
        FieldArrayProps<TFieldValues, TResponseData, TFieldArrayName>,
        "of"
      >,
    ) => JSXOutput;
  },
];

export function useForm(
  options: FormOptions<FieldValues, ResponseData> & {
    resetOnSuccess?: boolean;
  },
): [
  FormStore<FieldValues, ResponseData>,
  {
    Form: (
      props: Omit<FormProps<FieldValues, ResponseData>, "of" | "action">,
    ) => JSXOutput;
    Field: (
      props: Omit<FieldProps<FieldValues, ResponseData, string>, "of">,
    ) => JSXOutput;
    FieldArray: (
      props: Omit<FieldArrayProps<FieldValues, ResponseData, string>, "of">,
    ) => JSXOutput;
  },
] {
  const { loader, resetOnSuccess = true } = options;
  const formStore = originalUseForm<FieldValues, ResponseData>({
    validateOn: "blur",
    ...options,
  });
  const [form] = formStore;

  useTask$(({ track }) => {
    const response = track(() => form.response);
    if (!response?.status) return;

    if (response.status === "success") {
      toast.success(response.message ?? "Success!");
      if (resetOnSuccess) {
        reset(form, {
          initialValues: loader.value,
          keepResponse: true,
        });
      }
    } else {
      console.error("Form error:", response);
      toast.error(response.message ?? "Something went wrong");
    }
  });

  return formStore;
}
